import classNames from 'classnames';
import { ReactElement, ReactNode } from 'react';
import { NoValue } from '../../../sharedComponents/NoValue';
import { ServiceOrder, ServiceOrderStatus } from '../serviceOrder.types';
import { ServiceOrdersTableColumns } from './ServiceOrdersTableColumns';
import { AlertIcon, DateTime } from '../../../sharedComponents/table/BaseTableComponents';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { getSelectedServiceOrder, serviceOrdersSlice } from '../../../reducers/serviceOrdersSlice';
import { getRowAlertConfiguration } from './serviceOrderTableRowAlertStyling';
import { AlertConfig } from '../../../sharedComponents/common.types';
import { ServiceOrderTableActionButton } from './ServiceOrderTableActionButton';
import { StatusLabel } from '../../../sharedComponents/StatusLabel';
import { getServiceNameByType } from '../serviceOrderUtils';
import { ColumnDefinition } from '../../../sharedComponents/table/SortedTableHeader';

export const TableDataContent = (props: {
    column: ServiceOrdersTableColumns;
    serviceOrder: ServiceOrder;
}): ReactElement => {
    const { serviceOrder } = props;
    switch (props.column) {
        case ServiceOrdersTableColumns.EXTERNAL_ID:
            return <span>{serviceOrder.externalOrderId}</span>;
        case ServiceOrdersTableColumns.STATUS:
            return <StatusLabel status={serviceOrder.status} />;
        case ServiceOrdersTableColumns.VIN:
            return (
                <strong>
                    {serviceOrder.vehicle.id.vin ?? serviceOrder.vehicle.id.productionNumber ?? <NoValue />}
                </strong>
            );
        case ServiceOrdersTableColumns.LOCATION_ID:
            return <span>{serviceOrder.serviceLocation.identifier ?? <NoValue />}</span>;
        case ServiceOrdersTableColumns.LOCATION_CITY:
            return <span>{serviceOrder.serviceLocation.address?.city ?? <NoValue />}</span>;
        case ServiceOrdersTableColumns.MANUFACTURER:
            return <span>{serviceOrder.vehicle.model?.manufacturer ?? <NoValue />}</span>;
        case ServiceOrdersTableColumns.MODEL:
            return serviceOrder.vehicle.model === undefined ? (
                <NoValue />
            ) : (
                <span>
                    <span className="rioglyph rioglyph-car text-size-18 padding-right-5" />
                    {serviceOrder.vehicle.model?.name}
                </span>
            );
        case ServiceOrdersTableColumns.EXECUTION_DATE_START:
            return serviceOrder.startedAt === undefined ? (
                <NoValue
                    messageId={
                        serviceOrder.status === ServiceOrderStatus.CANCELLED
                            ? undefined
                            : 'outboundPortal.serviceOrder.table.executionStartDate.missing'
                    }
                />
            ) : (
                <DateTime dateTime={serviceOrder.startedAt} timeZone={serviceOrder.serviceLocation.timeZone} />
            );
        case ServiceOrdersTableColumns.EXECUTION_DATE_END:
            return serviceOrder.completedAt === undefined ? (
                <NoValue
                    messageId={
                        serviceOrder.status === ServiceOrderStatus.CANCELLED
                            ? undefined
                            : 'outboundPortal.serviceOrders.table.executionEndDate.missing'
                    }
                />
            ) : (
                <DateTime dateTime={serviceOrder.completedAt} timeZone={serviceOrder.serviceLocation.timeZone} />
            );
        case ServiceOrdersTableColumns.ORDER_ISSUE_DATE:
            return <DateTime dateTime={serviceOrder.orderIssuedAt} timeZone={serviceOrder.serviceLocation.timeZone} />;
        case ServiceOrdersTableColumns.SERVICE_CODE:
            return <span>{serviceOrder.type}</span>;
        case ServiceOrdersTableColumns.SERVICE_NAME:
            return (
                <strong>
                    <span className="rioglyph rioglyph-component-custom text-size-18 padding-right-5" />
                    {getServiceNameByType(serviceOrder.type)}
                </strong>
            );
        case ServiceOrdersTableColumns.SERVICE_DESCRIPTION:
            return serviceOrder.description === undefined ? <NoValue /> : <span>{serviceOrder.description}</span>;
        case ServiceOrdersTableColumns.EXTERNAL_OPTION_ID:
            return serviceOrder.externalOptionId === undefined ? (
                <NoValue />
            ) : (
                <span>{serviceOrder.externalOptionId}</span>
            );
        default:
            throw new Error('exhaustive switch exception - table content');
    }
};

const TableRowData = (props: {
    column: ServiceOrdersTableColumns;
    serviceOrder: ServiceOrder;
    highlightConfig: AlertConfig | undefined;
}) => {
    const { column, serviceOrder } = props;
    const highlightConfig = props.highlightConfig !== undefined ? [props.highlightConfig] : [];

    switch (column) {
        case ServiceOrdersTableColumns.HIGHLIGHT:
            return (
                <td key={column}>
                    <AlertIcon rowConfig={highlightConfig} />
                </td>
            );
        case ServiceOrdersTableColumns.EXTERNAL_ID:
        case ServiceOrdersTableColumns.MODEL:
        case ServiceOrdersTableColumns.SERVICE_NAME:
        case ServiceOrdersTableColumns.SERVICE_DESCRIPTION:
            return (
                <td className="ellipsis-1" key={column}>
                    <TableDataContent column={column} serviceOrder={serviceOrder} />
                </td>
            );
        case ServiceOrdersTableColumns.ACTION_BUTTON:
            return (
                <td className="text-center" key={column}>
                    <ServiceOrderTableActionButton serviceOrder={serviceOrder} />
                </td>
            );
        default:
            return (
                <td key={column}>
                    <TableDataContent column={column} serviceOrder={serviceOrder} />
                </td>
            );
    }
};

export const ServiceOrderTableRow = (props: {
    serviceOrder: ServiceOrder;
    columns: ColumnDefinition<ServiceOrdersTableColumns>[];
    selectionCell?: ReactNode;
}) => {
    const { id, status, hasUnacknowledgedExternalChanges } = props.serviceOrder;
    const selectedServiceOrder = useAppSelector(getSelectedServiceOrder);
    const dispatch = useAppDispatch();
    const highlightConfig = getRowAlertConfiguration(status, hasUnacknowledgedExternalChanges);

    const isSelected = selectedServiceOrder?.id === id;

    const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
        /* Ignore click events from inside a checkbox cell */
        const clickedElement = event.target as HTMLElement;
        const isCheckboxCell = clickedElement.closest('.table-checkbox');
        if (isCheckboxCell) {
            return;
        }

        if (isSelected) {
            dispatch(serviceOrdersSlice.actions.deselectServiceOrder());
        } else {
            dispatch(serviceOrdersSlice.actions.selectServiceOrder(props.serviceOrder));
        }
    };

    return (
        <tr
            key={id}
            onClick={handleRowClick}
            className={classNames('cursor-pointer', isSelected && 'active', highlightConfig?.highlight)}
        >
            {props.selectionCell}
            {props.columns.map((def) => {
                return (
                    <TableRowData
                        key={def.column}
                        column={def.column}
                        serviceOrder={props.serviceOrder}
                        highlightConfig={highlightConfig}
                    />
                );
            })}
        </tr>
    );
};
